import React, { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../components/ui/select';
import { adminAPI } from '../lib/api';
import { formatDate } from '../lib/utils';
import {
  Clock, Download, Loader2, AlertTriangle, CheckCircle2, XCircle, Calendar,
  TrendingUp, Users, FileSpreadsheet, ChevronRight,
} from 'lucide-react';
import { toast } from 'sonner';

const fmtHM = (seconds) => {
  if (!seconds || seconds < 0) return '0h 0m';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  return `${h}h ${m.toString().padStart(2, '0')}m`;
};

const fmtClockUk = (iso) => {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit', timeZone: 'Europe/London' });
  } catch { return iso; }
};

const ANOMALY_META = {
  forgotten_clock_out: { label: 'Forgotten Clock-Out', cls: 'bg-rose-100 text-rose-700 border-rose-200' },
  over_12h: { label: '>12h Shift', cls: 'bg-orange-100 text-orange-700 border-orange-200' },
  weekend: { label: 'Weekend', cls: 'bg-purple-100 text-purple-700 border-purple-200' },
  auto_closed_2000: { label: 'Auto-closed 20:00', cls: 'bg-amber-100 text-amber-700 border-amber-200' },
  overtime: { label: 'Overtime', cls: 'bg-blue-100 text-blue-700 border-blue-200' },
};

export const AdminTimeSheets = () => {
  const [weeklyData, setWeeklyData] = useState(null);
  const [allShifts, setAllShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [weeks, setWeeks] = useState(6);
  const [approving, setApproving] = useState(null);
  const [approvalDlg, setApprovalDlg] = useState({ open: false, shift: null });
  const [approvalNote, setApprovalNote] = useState('');
  const [selectedEngineer, setSelectedEngineer] = useState('all');

  const fetchData = async () => {
    setLoading(true);
    try {
      const [weekly, shifts] = await Promise.all([
        adminAPI.timeSheetsWeekly(weeks),
        adminAPI.timeSheetsShifts({ days: weeks * 7 }),
      ]);
      setWeeklyData(weekly.data);
      setAllShifts(shifts.data || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not load time sheets');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [weeks]);

  const decide = async (decision) => {
    const shift = approvalDlg.shift;
    if (!shift) return;
    setApproving(shift.id);
    try {
      await adminAPI.timeSheetsApprove(shift.id, decision, approvalNote);
      toast.success(`Shift ${decision}`);
      setApprovalDlg({ open: false, shift: null });
      setApprovalNote('');
      fetchData();
    } catch (e) {
      toast.error('Could not update');
    } finally {
      setApproving(null);
    }
  };

  const pendingShifts = allShifts.filter(s => s.approval_status === 'pending');
  const anomalyShifts = allShifts.filter(s => {
    const wd = s.work_date ? new Date(s.work_date) : null;
    const wkday = wd ? wd.getDay() : -1;
    return (
      (!s.clock_out_at && s.work_date && new Date(s.work_date) < new Date(new Date().toDateString())) ||
      (s.duration_seconds || 0) > 12 * 3600 ||
      wkday === 0 || wkday === 6 ||
      s.auto_closed
    );
  });

  const visibleEngineers = (weeklyData?.engineers || []).filter(e =>
    selectedEngineer === 'all' || e.engineer_account_id === selectedEngineer
  );

  return (
    <Layout>
      <div className="space-y-6 animate-fade-in" data-testid="admin-time-sheets-page">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 font-['Manrope'] flex items-center gap-3">
              <Clock className="w-7 h-7 text-blue-600" />
              Engineer Time Sheets
            </h1>
            <p className="text-slate-500 mt-1">
              Weekly hours, overtime approvals, and payroll-ready CSV export.
            </p>
          </div>
          <div className="flex gap-2 items-center">
            <Select value={String(weeks)} onValueChange={(v) => setWeeks(parseInt(v, 10))}>
              <SelectTrigger className="w-36" data-testid="weeks-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="2">Last 2 weeks</SelectItem>
                <SelectItem value="4">Last 4 weeks</SelectItem>
                <SelectItem value="6">Last 6 weeks</SelectItem>
                <SelectItem value="12">Last 12 weeks</SelectItem>
                <SelectItem value="26">Last 6 months</SelectItem>
              </SelectContent>
            </Select>
            <Button asChild variant="outline" data-testid="export-csv-btn">
              <a href={adminAPI.timeSheetsCsvUrl(weeks * 7)} download>
                <Download className="w-4 h-4 mr-2" /> Export CSV
              </a>
            </Button>
          </div>
        </div>

        {/* Top stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card className="border-slate-200" data-testid="stat-total-hours">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Total Hours</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">
                    {fmtHM(weeklyData?.total_seconds || 0)}
                  </p>
                  <p className="text-xs text-slate-500 mt-0.5">Last {weeks} week{weeks === 1 ? '' : 's'}</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-blue-100 flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200" data-testid="stat-engineers">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Engineers</p>
                  <p className="text-2xl font-bold text-slate-900 mt-1">{weeklyData?.engineers?.length || 0}</p>
                  <p className="text-xs text-slate-500 mt-0.5">With logged shifts</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
                  <Users className="w-5 h-5 text-indigo-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200" data-testid="stat-pending">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Pending Approval</p>
                  <p className="text-2xl font-bold text-amber-700 mt-1">{pendingShifts.length}</p>
                  <p className="text-xs text-slate-500 mt-0.5">Overtime shifts</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <AlertTriangle className="w-5 h-5 text-amber-600" />
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-slate-200" data-testid="stat-anomalies">
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <p className="text-xs uppercase tracking-wide text-slate-500 font-medium">Anomalies</p>
                  <p className="text-2xl font-bold text-rose-700 mt-1">{anomalyShifts.length}</p>
                  <p className="text-xs text-slate-500 mt-0.5">Forgotten / over-12h / weekend</p>
                </div>
                <div className="w-10 h-10 rounded-xl bg-rose-100 flex items-center justify-center">
                  <Calendar className="w-5 h-5 text-rose-600" />
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter */}
        {(weeklyData?.engineers || []).length > 0 && (
          <Select value={selectedEngineer} onValueChange={setSelectedEngineer}>
            <SelectTrigger className="w-72" data-testid="engineer-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All engineers</SelectItem>
              {weeklyData.engineers.map(e => (
                <SelectItem key={e.engineer_account_id} value={e.engineer_account_id} data-testid={`engineer-option-${e.engineer_account_id}`}>
                  {e.name} ({fmtHM(e.total_seconds)})
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        {/* Pending approvals */}
        {pendingShifts.length > 0 && (
          <Card className="border-amber-200 bg-amber-50/40" data-testid="pending-approvals-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2 text-amber-900">
                <AlertTriangle className="w-5 h-5" /> Pending Overtime Approvals ({pendingShifts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {pendingShifts.slice(0, 20).map(s => (
                  <div key={s.id} className="p-3 rounded-lg border border-amber-200 bg-white flex items-center justify-between" data-testid={`pending-${s.id}`}>
                    <div className="min-w-0 flex-1">
                      <p className="text-sm font-semibold text-slate-900">{s.engineer_name}</p>
                      <p className="text-xs text-slate-500">
                        {formatDate(s.work_date)} · {fmtClockUk(s.clock_in_at)} → {s.clock_out_at ? fmtClockUk(s.clock_out_at) : 'still open'} · <strong>{fmtHM(s.duration_seconds)}</strong>
                      </p>
                      {s.clock_out_note && <p className="text-[11px] text-slate-400 mt-0.5 italic">"{s.clock_out_note}"</p>}
                    </div>
                    <Button
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-700 text-white ml-2"
                      onClick={() => { setApprovalDlg({ open: true, shift: s }); setApprovalNote(''); }}
                      disabled={approving === s.id}
                      data-testid={`review-${s.id}`}
                    >
                      Review <ChevronRight className="w-3 h-3 ml-1" />
                    </Button>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Weekly grid per engineer */}
        <Card className="border-slate-200">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-slate-600" /> Weekly Hours
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-5 h-5 animate-spin text-slate-400" />
              </div>
            ) : visibleEngineers.length === 0 ? (
              <p className="text-center text-slate-400 py-12 text-sm">No shifts logged yet.</p>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="min-w-[180px]">Engineer</TableHead>
                      {(visibleEngineers[0]?.weeks || []).map(w => (
                        <TableHead key={w.week} className="text-center">{w.week}</TableHead>
                      ))}
                      <TableHead className="text-right">Period Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {visibleEngineers.map(e => (
                      <TableRow key={e.engineer_account_id} data-testid={`engineer-row-${e.engineer_account_id}`}>
                        <TableCell>
                          <p className="text-sm font-medium text-slate-900">{e.name}</p>
                          <p className="text-[11px] text-slate-500">{e.email}</p>
                          <div className="flex gap-1 mt-1">
                            {e.pending_approvals > 0 && (
                              <Badge className="bg-amber-100 text-amber-700 border-amber-200 border text-[10px]">
                                {e.pending_approvals} pending
                              </Badge>
                            )}
                            {e.anomaly_count > 0 && (
                              <Badge className="bg-rose-100 text-rose-700 border-rose-200 border text-[10px]">
                                {e.anomaly_count} anomal{e.anomaly_count === 1 ? 'y' : 'ies'}
                              </Badge>
                            )}
                          </div>
                        </TableCell>
                        {(visibleEngineers[0]?.weeks || []).map(refWeek => {
                          const w = e.weeks.find(x => x.week === refWeek.week);
                          if (!w) return <TableCell key={refWeek.week} className="text-center text-slate-300 text-xs">—</TableCell>;
                          return (
                            <TableCell key={w.week} className="text-center" data-testid={`cell-${e.engineer_account_id}-${w.week}`}>
                              <p className="text-sm font-semibold tabular-nums">{fmtHM(w.seconds)}</p>
                              <p className="text-[10px] text-slate-500">{w.days} day{w.days === 1 ? '' : 's'}</p>
                              {w.overtime_pending > 0 && (
                                <Badge className="bg-amber-100 text-amber-700 border-amber-200 border text-[9px] mt-0.5">OT {w.overtime_pending}</Badge>
                              )}
                            </TableCell>
                          );
                        })}
                        <TableCell className="text-right font-bold tabular-nums">{fmtHM(e.total_seconds)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Anomalies */}
        {anomalyShifts.length > 0 && (
          <Card className="border-slate-200" data-testid="anomalies-card">
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-rose-500" /> Anomalies ({anomalyShifts.length})
              </CardTitle>
            </CardHeader>
            <CardContent>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Date</TableHead>
                    <TableHead>Engineer</TableHead>
                    <TableHead>In</TableHead>
                    <TableHead>Out</TableHead>
                    <TableHead>Duration</TableHead>
                    <TableHead>Flags</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {anomalyShifts.slice(0, 30).map(s => {
                    const wd = new Date(s.work_date);
                    const wkend = wd.getDay() === 0 || wd.getDay() === 6;
                    const forgot = !s.clock_out_at && wd < new Date(new Date().toDateString());
                    const over12 = (s.duration_seconds || 0) > 12 * 3600;
                    return (
                      <TableRow key={s.id} data-testid={`anomaly-${s.id}`}>
                        <TableCell className="text-xs">{formatDate(s.work_date)}</TableCell>
                        <TableCell className="text-xs">{s.engineer_name}</TableCell>
                        <TableCell className="text-xs font-mono">{fmtClockUk(s.clock_in_at)}</TableCell>
                        <TableCell className="text-xs font-mono">{s.clock_out_at ? fmtClockUk(s.clock_out_at) : <span className="text-rose-500 font-semibold">— open</span>}</TableCell>
                        <TableCell className="text-xs tabular-nums">{fmtHM(s.duration_seconds)}</TableCell>
                        <TableCell>
                          <div className="flex gap-1 flex-wrap">
                            {forgot && <Badge className={`${ANOMALY_META.forgotten_clock_out.cls} border text-[10px]`}>{ANOMALY_META.forgotten_clock_out.label}</Badge>}
                            {over12 && <Badge className={`${ANOMALY_META.over_12h.cls} border text-[10px]`}>{ANOMALY_META.over_12h.label}</Badge>}
                            {wkend && <Badge className={`${ANOMALY_META.weekend.cls} border text-[10px]`}>{ANOMALY_META.weekend.label}</Badge>}
                            {s.auto_closed && <Badge className={`${ANOMALY_META.auto_closed_2000.cls} border text-[10px]`}>{ANOMALY_META.auto_closed_2000.label}</Badge>}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}

        {/* Approval dialog */}
        <Dialog open={approvalDlg.open} onOpenChange={(o) => !o && setApprovalDlg({ open: false, shift: null })}>
          <DialogContent className="max-w-md" data-testid="approval-dialog">
            <DialogHeader>
              <DialogTitle>Review Overtime Shift</DialogTitle>
              <DialogDescription>
                {approvalDlg.shift?.engineer_name} · {formatDate(approvalDlg.shift?.work_date)}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 text-sm">
              <p>
                <strong>In:</strong> {fmtClockUk(approvalDlg.shift?.clock_in_at)} · <strong>Out:</strong> {approvalDlg.shift?.clock_out_at ? fmtClockUk(approvalDlg.shift?.clock_out_at) : 'still open'}
              </p>
              <p><strong>Duration:</strong> {fmtHM(approvalDlg.shift?.duration_seconds)}</p>
              {approvalDlg.shift?.clock_in_note && <p className="text-xs text-slate-500 italic">"In: {approvalDlg.shift.clock_in_note}"</p>}
              {approvalDlg.shift?.clock_out_note && <p className="text-xs text-slate-500 italic">"Out: {approvalDlg.shift.clock_out_note}"</p>}
              <Input
                placeholder="Optional approval/rejection note"
                value={approvalNote}
                onChange={(e) => setApprovalNote(e.target.value)}
                data-testid="approval-note-input"
              />
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => decide('rejected')} disabled={approving} className="text-rose-600 hover:text-rose-700" data-testid="reject-btn">
                <XCircle className="w-4 h-4 mr-1" /> Reject
              </Button>
              <Button onClick={() => decide('approved')} disabled={approving} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="approve-btn">
                <CheckCircle2 className="w-4 h-4 mr-1" /> Approve
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminTimeSheets;
